import { useEffect, useEffectEvent } from 'react';

import { isEditableTarget, isMacPlatform } from '../dom';

export interface HotkeyOptions {
  /** Require the platform modifier (⌘ on macOS, Ctrl elsewhere). */
  mod?: boolean;
  shift?: boolean;
  alt?: boolean;
  /** Fire even when focus is inside an input / editor (default: only when `mod` is set). */
  allowInEditable?: boolean;
  enabled?: boolean;
}

/**
 * Whether `event` is `key`. The character the layout produces decides (`KeyboardEvent.key`, so Dvorak or AZERTY users
 * press the letter where their layout has it); when that is not a Latin letter or digit — a Russian or Ukrainian layout
 * turns Ctrl+K into `key === 'л'` — letters and digits match by physical key (`KeyboardEvent.code`) instead.
 */
export function matchesHotkey(event: KeyboardEvent, key: string): boolean {
  const wanted = key.toLowerCase();
  const pressed = event.key.toLowerCase();
  if (pressed === wanted) return true;
  if (/^[a-z0-9]$/u.test(pressed)) return false;
  if (/^[a-z]$/u.test(wanted)) return event.code === `Key${wanted.toUpperCase()}`;
  if (/^[0-9]$/u.test(wanted)) return event.code === `Digit${wanted}`;
  return false;
}

/**
 * Registers a global keyboard shortcut (see {@link matchesHotkey} for how `key` is matched).
 * Example: `useHotkey('k', openPalette, { mod: true })` → ⌘K / Ctrl+K. Keys an element handles itself (it calls
 * `stopPropagation`, e.g. the rich text editor's link shortcut) do not reach global shortcuts.
 */
export function useHotkey(
  key: string,
  handler: (event: KeyboardEvent) => void,
  options: HotkeyOptions = {},
): void {
  const { mod = false, shift = false, alt = false, enabled = true } = options;
  const allowInEditable = options.allowInEditable ?? mod;
  const onKey = useEffectEvent((event: KeyboardEvent) => {
    if (!matchesHotkey(event, key)) return;
    const modPressed = isMacPlatform() ? event.metaKey : event.ctrlKey;
    if (mod !== modPressed || shift !== event.shiftKey || alt !== event.altKey) return;
    if (!allowInEditable && isEditableTarget(event.target)) return;
    event.preventDefault();
    handler(event);
  });

  useEffect(() => {
    if (!enabled) return;
    const listener = (event: KeyboardEvent) => onKey(event);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [enabled]);
}
